import React, { useState, useEffect, useRef } from 'react';
import find from 'lodash.find';
import PropTypes from 'prop-types';
import { isMobile } from 'react-device-detect';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';

import SelectMenu from './components/select';
import { useUserDataContext } from '../../wiring/user-provider';

import Insta from '../../styles/social/instagram';
import Text from '../../styles/social/text';
import Facebook from '../../styles/social/facebook';
import LinkedIn from '../../styles/social/linkedin';
import Bluesky from '../../styles/social/bluesky';

import DifficulityBlock from './components/difficulty-block';
import Icons from './icons';
import Styles from './styles';

import allStatesData from '../../utils/state-data';
import useI18n from '../../utils/use-i18n';
import { shouldShowWebShare, webShareUrl } from '../../utils/web-share';
import getFacebookShareUrl from '../../utils/facebook-share';
import openLinkedInShare from '../../utils/linkedin-share';
import getBlueskyShareUrl from '../../utils/bluesky-share';
import shareToInstagramStory from '../../utils/instagram-story-share';
import ContentEnglish from './i18n/en';
import ContentSpanish from './i18n/es';

const content = {
    en: ContentEnglish,
    es: ContentSpanish,
};

const Home = ({ classes }) => {
    const { selectedState, selectedLanguage } = useUserDataContext();
    const [statesData, setStatesData] = useState(null);
    const [selectedStateData, setSelectedStateData] = useState(null);
    const [qualifications, setQualifications] = useState(null);
    const [exceptions, setExceptions] = useState(null);
    const [deadlines, setDeadlines] = useState(null);
    const [instructions, setInstructions] = useState(null);
    const [militaryInstructions, setMilitaryInstructions] = useState(null);
    const [postalServiceInstructions, setPostalServiceInstructions] =
        useState(null);
    const [resources, setResources] = useState(null);
    const [trust, setTrust] = useState(null);

    const showTextShare = shouldShowWebShare();

    const isComponentMounted = useRef(true);
    useEffect(() => {
        if (isComponentMounted.current) {
            setStatesData(allStatesData);
        }
        return () => {
            isComponentMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (statesData && selectedState) {
            const state = find(statesData, ['name', selectedState]);
            if (state) setSelectedStateData(state);
        }
    }, [selectedState, statesData]);

    useEffect(() => {
        if (selectedStateData) {
            if (selectedLanguage === 'es') {
                setQualifications(
                    selectedStateData.qualifications_es ||
                        selectedStateData.qualifications,
                );
                setExceptions(
                    selectedStateData.exceptions_es ||
                        selectedStateData.exceptions,
                );
                setDeadlines(
                    selectedStateData.deadlines_es ||
                        selectedStateData.deadlines,
                );
                setInstructions(
                    selectedStateData.instructions_es ||
                        selectedStateData.instructions,
                );
                setMilitaryInstructions(
                    selectedStateData.military_instructions_es ||
                        selectedStateData.military_instructions,
                );
                setResources(
                    selectedStateData.resources_es ||
                        selectedStateData.resources,
                );
                setTrust(selectedStateData.trust_es || selectedStateData.trust);
                setPostalServiceInstructions(
                    selectedStateData.postal_service_instructions_es ||
                        selectedStateData.postal_service_instructions,
                );
            } else {
                setQualifications(selectedStateData.qualifications);
                setExceptions(selectedStateData.exceptions);
                setDeadlines(selectedStateData.deadlines);
                setInstructions(selectedStateData.instructions);
                setMilitaryInstructions(
                    selectedStateData.military_instructions,
                );
                setResources(selectedStateData.resources);
                setTrust(selectedStateData.trust);
                setPostalServiceInstructions(
                    selectedStateData.postal_service_instructions,
                );
            }
        }
    }, [selectedLanguage, selectedStateData]);

    // when content updates, scroll it
    useEffect(() => {
        if (qualifications) {
            document
                .getElementById('stateContent')
                .scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    }, [qualifications]);

    // conditional Text
    const QualificationTitle = useI18n('qualification_title', content);
    const QualificationOrTitle = useI18n('qualification_or_title', content);
    const MilitaryTitle = useI18n('military_title', content);
    const DeadlinesTitle = useI18n('deadlines_title', content);
    const ResourcesTitle = useI18n('resources_title', content);
    const TrustTitle = useI18n('trust_title', content);
    const InstructionsTitle = useI18n('instructions_title', content);
    const PostalServiceInstructionsTitle = useI18n(
        'postal_service_instructions_title',
        content,
    );
    const ShareTitle = useI18n('share_title', content);
    const ShareDescription = useI18n(
        'share_description',
        content,
        selectedState,
    );
    const VideoTitle = useI18n('video_title', content);

    return (
        <div className={classes.pageWrapper}>
            <section className={classes.topWrapper}>
                <div className="container">
                    <div className="row">
                        <div className="col">
                            <Typography
                                variant="body1"
                                className={classes.subtitle}>
                                {useI18n('subtitle', content)}
                            </Typography>
                            <Typography
                                variant="body1"
                                className={classes.electionBanner}>
                                {useI18n('election_banner_title', content)}
                            </Typography>
                            <Typography
                                variant="h1"
                                className="d-inline mr-0 mr-sm-4"
                                style={{ verticalAlign: 'top' }}>
                                {useI18n('title_1', content)}{' '}
                                <br className="d-none d-sm-inline" />
                                {useI18n('title_2', content)}
                            </Typography>
                            <SelectMenu />
                        </div>
                    </div>
                </div>
            </section>
            <div className={classes.contentBuffer} id="stateContent" />
            {selectedState && qualifications && (
                <section className={classes.contentWrapper}>
                    <div className="container">
                        <div className="row pb-4 align-items-baseline">
                            <div className="col-auto">
                                <Typography
                                    variant="h1"
                                    component="span"
                                    style={{ verticalAlign: 'sub' }}>
                                    {selectedState}
                                </Typography>
                            </div>
                            <div className="col">
                                <DifficulityBlock
                                    difficulty={selectedStateData.difficulty}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-8">
                                {/* qualifications/exceptions/military instructions */}
                                <div className="row">
                                    <div className="col p-0 p-sm-2">
                                        <div className={classes.contentSection}>
                                            {!!qualifications &&
                                                qualifications.length > 0 && (
                                                    <Typography
                                                        variant="h3"
                                                        style={{
                                                            paddingBottom: 15,
                                                        }}>
                                                        {QualificationTitle}
                                                    </Typography>
                                                )}
                                            {!!qualifications &&
                                                qualifications.length > 0 &&
                                                qualifications.map((item) => (
                                                    <div
                                                        className="row align-items-center no-gutters pb-2"
                                                        key={`${selectedState}-qualification-${item}`}>
                                                        <div className="col-auto">
                                                            <img
                                                                src={
                                                                    Icons.eligibility
                                                                }
                                                                alt=""
                                                                className={
                                                                    classes.contentIcon
                                                                }
                                                            />
                                                        </div>
                                                        <div className="col">
                                                            <Typography
                                                                variant="body1"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: item,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            {/* exceptions */}
                                            {!!exceptions &&
                                                exceptions.length > 0 && (
                                                    <Typography
                                                        variant="h3"
                                                        style={{
                                                            paddingTop: 5,
                                                            paddingBottom: 5,
                                                        }}>
                                                        {QualificationOrTitle}
                                                    </Typography>
                                                )}
                                            {!!exceptions &&
                                                exceptions.length > 0 &&
                                                exceptions.map((item) => (
                                                    <div
                                                        className="row align-items-center no-gutters pb-2"
                                                        key={`${selectedState}-exceptions-${item}`}>
                                                        <div className="col-auto">
                                                            <img
                                                                src={
                                                                    Icons.eligibility
                                                                }
                                                                alt=""
                                                                className={
                                                                    classes.contentIcon
                                                                }
                                                            />
                                                        </div>
                                                        <div className="col">
                                                            <Typography
                                                                variant="body1"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: item,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                        {/* military instructions */}
                                        {!!militaryInstructions &&
                                            militaryInstructions.length > 0 && (
                                                <div
                                                    className={
                                                        classes.contentSection
                                                    }>
                                                    <Typography
                                                        variant="h3"
                                                        style={{
                                                            paddingBottom: 15,
                                                        }}>
                                                        {MilitaryTitle}
                                                    </Typography>
                                                    {militaryInstructions.map(
                                                        (item) => (
                                                            <div
                                                                className="row align-items-center no-gutters pb-2"
                                                                key={`${selectedState}-military-${item}`}>
                                                                <div className="col-auto">
                                                                    <img
                                                                        src={
                                                                            Icons.ballotbox
                                                                        }
                                                                        alt=""
                                                                        className={
                                                                            classes.contentIcon
                                                                        }
                                                                    />
                                                                </div>
                                                                <div className="col">
                                                                    <Typography
                                                                        variant="body1"
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: item,
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                        <div className={classes.contentSection}>
                                            {!!deadlines &&
                                                deadlines.length > 0 && (
                                                    <Typography
                                                        variant="h3"
                                                        style={{
                                                            paddingBottom: 15,
                                                        }}>
                                                        {DeadlinesTitle}
                                                    </Typography>
                                                )}
                                            {!!deadlines &&
                                                deadlines.length > 0 &&
                                                deadlines.map((item) => (
                                                    <div
                                                        className="row align-items-center no-gutters pb-2"
                                                        key={`${selectedState}-deadlines-${item}`}>
                                                        <div className="col-auto">
                                                            <img
                                                                src={
                                                                    Icons.deadlines
                                                                }
                                                                alt=""
                                                                className={
                                                                    classes.contentIcon
                                                                }
                                                            />
                                                        </div>
                                                        <div className="col">
                                                            <Typography
                                                                variant="body1"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: item,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                        {/* important instructions */}
                                        <div className={classes.contentSection}>
                                            <Typography
                                                variant="h3"
                                                style={{
                                                    paddingBottom: 15,
                                                    textTransform: 'uppercase',
                                                }}>
                                                {PostalServiceInstructionsTitle}
                                            </Typography>
                                            {!!postalServiceInstructions &&
                                                postalServiceInstructions.length >
                                                    0 &&
                                                postalServiceInstructions.map(
                                                    (item) => (
                                                        <div
                                                            className="row align-items-center no-gutters pb-2"
                                                            key={`${selectedState}-postal-service-instructions-${item}`}>
                                                            <div className="col-auto">
                                                                <img
                                                                    src={
                                                                        Icons.mail
                                                                    }
                                                                    alt=""
                                                                    className={
                                                                        classes.contentIcon
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col">
                                                                <Typography
                                                                    variant="body1"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: item,
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ),
                                                )}
                                        </div>
                                        <div className={classes.contentSection}>
                                            <Typography
                                                variant="h3"
                                                style={{ paddingBottom: 15 }}>
                                                {InstructionsTitle}
                                            </Typography>
                                            {!!instructions &&
                                                instructions.length > 0 &&
                                                instructions.map((item) => (
                                                    <div
                                                        className="row align-items-center no-gutters pb-2"
                                                        key={`${selectedState}-instruction-${item.icon}-${item.text}`}>
                                                        <div className="col-auto">
                                                            <img
                                                                src={
                                                                    Icons[
                                                                        item
                                                                            .icon
                                                                    ]
                                                                }
                                                                alt=""
                                                                className={
                                                                    classes.contentIcon
                                                                }
                                                            />
                                                        </div>
                                                        <div className="col">
                                                            <Typography
                                                                variant="body1"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: item.text,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="row">
                                    <div className="col p-0 p-sm-2">
                                        <div className={classes.contentSection}>
                                            <Typography variant="h3">
                                                {ShareTitle}
                                            </Typography>
                                            <Typography variant="body1">
                                                {ShareDescription}
                                            </Typography>
                                            <div className="pt-2 pb-4">
                                                <button
                                                    type="button"
                                                    className={
                                                        classes.socialBlock
                                                    }
                                                    onClick={
                                                        shareToInstagramStory
                                                    }
                                                    aria-label="Share to Instagram Story">
                                                    <Insta />
                                                </button>
                                                {showTextShare && (
                                                    <button
                                                        type="button"
                                                        className={
                                                            classes.socialBlock
                                                        }
                                                        onClick={webShareUrl}
                                                        aria-label="Share via Text Message">
                                                        <Text />
                                                    </button>
                                                )}
                                                <a
                                                    className={
                                                        classes.socialBlock
                                                    }
                                                    href={getFacebookShareUrl(
                                                        selectedLanguage,
                                                        selectedState,
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Share on Facebook">
                                                    <Facebook />
                                                </a>
                                                <button
                                                    type="button"
                                                    className={
                                                        classes.socialBlock
                                                    }
                                                    onClick={() =>
                                                        openLinkedInShare(
                                                            selectedLanguage,
                                                            selectedState,
                                                        )
                                                    }
                                                    aria-label="Share on LinkedIn">
                                                    <LinkedIn />
                                                </button>
                                                <a
                                                    className={
                                                        classes.socialBlock
                                                    }
                                                    href={getBlueskyShareUrl(
                                                        selectedLanguage,
                                                        selectedState,
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Share on Bluesky">
                                                    <Bluesky />
                                                </a>
                                            </div>
                                            {isMobile && (
                                                <Typography
                                                    variant="caption"
                                                    component="p"
                                                    className={
                                                        classes.instaHint
                                                    }>
                                                    Sharing to Instagram? Choose
                                                    it from the menu that pops
                                                    up next.
                                                </Typography>
                                            )}
                                            {!!resources &&
                                                resources.length > 0 && (
                                                    <Typography
                                                        variant="h3"
                                                        className="pt-4">
                                                        {ResourcesTitle}
                                                    </Typography>
                                                )}
                                            {!!resources &&
                                                resources.length > 0 &&
                                                resources.map((item) => (
                                                    <Typography
                                                        variant="body1"
                                                        key={`${selectedState}-resources-${item}`}
                                                        dangerouslySetInnerHTML={{
                                                            __html: item,
                                                        }}
                                                    />
                                                ))}
                                            {!!trust && trust.length > 0 && (
                                                <Typography
                                                    variant="h3"
                                                    className="pt-4">
                                                    {TrustTitle}
                                                </Typography>
                                            )}
                                            {!!trust &&
                                                trust.length > 0 &&
                                                trust.map((item) => (
                                                    <Typography
                                                        variant="body1"
                                                        key={`${selectedState}-trust-${item}`}
                                                        dangerouslySetInnerHTML={{
                                                            __html: item,
                                                        }}
                                                    />
                                                ))}

                                            <Typography
                                                variant="h3"
                                                style={{
                                                    paddingBottom: 15,
                                                    paddingTop: 30,
                                                }}>
                                                {VideoTitle}
                                            </Typography>
                                            <div
                                                style={{
                                                    padding: '56.25% 0 0 0',
                                                    position: 'relative',
                                                }}>
                                                <iframe
                                                    src="https://player.vimeo.com/video/1226739175?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                                                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                    }}
                                                    title="Read The F*ing Directions - PSA 2"
                                                    aria-hidden
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

Home.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(Styles)(Home);
